<template>
  <b-modal
    ref="modal"
    :id="modalId"
    centered
    hide-footer
    title="Terms and Conditions - Service Bond Agreement (Ikatan Dinas)"
    size="xl"
    scrollable
    @hidden="onHidden"
  >
    <div class="terms-modal-content">
      <!-- Service Bond Warning Notice -->
      <div class="alert alert-warning mb-4" role="alert">
        <div class="d-flex align-items-center">
          <i class="fe fe-alert-triangle me-3" style="font-size: 1.5rem"></i>
          <div>
            <h6 class="alert-heading mb-1">
              Service Bond (Ikatan Dinas) Required
            </h6>
            <p class="mb-0 small">
              This training requires a service bond (Ikatan Dinas) commitment.
              Please read all terms and conditions carefully before proceeding.
            </p>
          </div>
        </div>
      </div>

      <!-- Reading Progress Indicator -->
      <div class="reading-progress mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="mb-0 font-weight-bold">Reading Progress</h6>
          <div class="d-flex align-items-center gap-2">
            <span
              class="badge"
              :class="hasReadAllTabs ? 'bg-success' : 'bg-secondary'"
            >
              <i
                class="fe"
                :class="hasReadAllTabs ? 'fe-check-circle' : 'fe-file-text'"
              ></i>
              {{
                hasReadAllTabs
                  ? "All Sections Read"
                  : "Please Read All Sections"
              }}
            </span>
          </div>
        </div>

        <div class="progress" style="height: 6px">
          <div
            class="progress-bar"
            :class="hasReadAllTabs ? 'bg-success' : 'bg-primary'"
            role="progressbar"
            :style="`width: ${readingProgress}%`"
            :aria-valuenow="readingProgress"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
        <small class="text-muted d-block mt-1">
          {{
            hasReadAllTabs
              ? "All sections completed"
              : `${tabsReadCount} of ${totalTabs} sections read`
          }}
        </small>
      </div>

      <!-- Terms and Conditions Tabs -->
      <div class="terms-tabs-container mb-4">
        <!-- Tab Navigation -->
        <ul class="nav nav-tabs mb-3" role="tablist">
          <li class="nav-item">
            <a
              class="nav-link active position-relative"
              data-bs-toggle="tab"
              href="#agreement-tab"
              @click="markTabAsViewed('agreement')"
            >
              Service Bond (Ikatan Dinas) Agreement
              <i
                v-if="viewedTabs.includes('agreement')"
                class="fe fe-check-circle text-success ms-2"
              ></i>
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link position-relative"
              data-bs-toggle="tab"
              href="#penalties-tab"
              @click="markTabAsViewed('penalties')"
            >
              Penalties & Consequences
              <i
                v-if="viewedTabs.includes('penalties')"
                class="fe fe-check-circle text-success ms-2"
              ></i>
            </a>
          </li>
        </ul>

        <!-- Tab Content -->
        <div class="tab-content">
          <!-- Service Bond Agreement Tab -->
          <div class="tab-pane fade show active" id="agreement-tab">
            <div
              class="terms-content"
              ref="agreementContent"
              @scroll="onTabScroll('agreement', $event)"
            >
              <div class="section mb-4">
                <h5 class="text-primary mb-3">
                  <i class="fe fe-file-text me-2"></i>
                  Article 1: Service Bond Commitment (Ikatan Dinas)
                </h5>
                <div class="content-box p-4 rounded border">
                  <p class="mb-3">
                    By enrolling in this training program, you (the "Employee")
                    agree to serve UGEMS University (the "Company") for a
                    minimum period of
                    <strong>twenty-four (24) consecutive months</strong>
                    after successful completion of the training program.
                  </p>
                  <ul class="mb-3">
                    <li>
                      Service bond is binding regardless of changes in position,
                      salary, or department within the Company
                    </li>
                    <li>
                      The commitment period cannot be transferred to other
                      companies or organizations
                    </li>
                  </ul>
                </div>
              </div>

              <div class="section mb-4">
                <h5 class="text-primary mb-3">
                  <i class="fe fe-book-open me-2"></i>
                  Article 2: Training Requirements & Standards
                </h5>
                <div class="content-box p-4 rounded border">
                  <p class="mb-3">
                    To successfully complete the training program, you must:
                  </p>
                  <div class="row">
                    <div class="col-md-6">
                      <ul class="mb-3">
                        <li>
                          <strong>Attendance:</strong> Minimum 95% attendance
                          for all scheduled sessions
                        </li>
                        <li>
                          <strong>Performance:</strong> Achieve minimum 80%
                          score in all assessments
                        </li>
                        <li>
                          <strong>Certification:</strong> Obtain all required
                          certifications within specified timeframe
                        </li>
                      </ul>
                    </div>
                    <div class="col-md-6">
                      <ul class="mb-3">
                        <li>
                          <strong>Conduct:</strong> Maintain professional
                          behavior throughout training
                        </li>
                        <li>
                          <strong>Participation:</strong> Active engagement in
                          all training activities
                        </li>
                        <li>
                          <strong>Assignments:</strong> Complete all required
                          assignments on time
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div class="alert alert-info">
                    <i class="fe fe-info me-2"></i>
                    <strong>Important:</strong> Failure to meet these
                    requirements may result in training termination and
                    immediate reimbursement obligation.
                  </div>
                </div>
              </div>

              <div class="section mb-4">
                <h5 class="text-primary mb-3">
                  <i class="fe fe-heart me-2"></i>
                  Article 4: Exceptions & Special Circumstances
                </h5>
                <div class="content-box p-4 rounded border">
                  <p class="mb-3">
                    Service bond obligations may be waived or modified in the
                    following cases:
                  </p>
                  <div class="exception-list">
                    <div class="exception-item d-flex align-items-start mb-3">
                      <div class="exception-icon me-3">
                        <i class="fe fe-plus-square text-danger"></i>
                      </div>
                      <div>
                        <h6 class="mb-1">Medical Conditions</h6>
                        <p class="mb-0 small text-muted">
                          Serious illness, permanent disability, or mental
                          health conditions certified by licensed medical
                          professionals.
                        </p>
                      </div>
                    </div>
                    <div class="exception-item d-flex align-items-start mb-3">
                      <div class="exception-icon me-3">
                        <i class="fe fe-alert-triangle text-warning"></i>
                      </div>
                      <div>
                        <h6 class="mb-1">Force Majeure</h6>
                        <p class="mb-0 small text-muted">
                          Natural disasters, war, or government actions that
                          make service impossible.
                        </p>
                      </div>
                    </div>
                    <div class="exception-item d-flex align-items-start mb-3">
                      <div class="exception-icon me-3">
                        <i class="fe fe-briefcase text-info"></i>
                      </div>
                      <div>
                        <h6 class="mb-1">Company Restructuring</h6>
                        <p class="mb-0 small text-muted">
                          Position elimination or company-initiated layoffs may
                          result in service bond waiver.
                        </p>
                      </div>
                    </div>
                    <div class="exception-item d-flex align-items-start mb-3">
                      <div class="exception-icon me-3">
                        <i class="fe fe-home text-success"></i>
                      </div>
                      <div>
                        <h6 class="mb-1">Family Emergencies</h6>
                        <p class="mb-0 small text-muted">
                          Extended family obligations may be considered for
                          partial or full waiver.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Penalties & Consequences Tab (PDF Viewer) -->
          <div class="tab-pane fade" id="penalties-tab">
            <div class="pdf-tab-container">
              <!-- PDF Header -->
              <div
                class="pdf-header d-flex justify-content-between align-items-center mb-3"
              >
                <h6 class="mb-0 font-weight-bold">
                  <i class="fe fe-file-text text-danger me-2"></i>
                  Penalties & Consequences Document
                </h6>
                <div class="d-flex align-items-center gap-2">
                  <span
                    class="badge"
                    :class="
                      tabScrollProgress.penalties >= 90
                        ? 'bg-success'
                        : 'bg-secondary'
                    "
                  >
                    <i
                      class="fe"
                      :class="
                        tabScrollProgress.penalties >= 90
                          ? 'fe-check-circle'
                          : 'fe-file-text'
                      "
                    ></i>
                    {{
                      tabScrollProgress.penalties >= 90
                        ? "Document Read"
                        : "Please Read Document"
                    }}
                  </span>
                </div>
              </div>

              <!-- PDF Iframe -->
              <div class="pdf-viewer-wrapper-tab">
                <div
                  class="pdf-scroll-container-tab"
                  ref="penaltiesContent"
                  @scroll="onTabScroll('penalties', $event)"
                >
                  <iframe
                    ref="pdfIframePenalties"
                    :src="`/dummy-terms-conditions.pdf#toolbar=0&navpanes=0&scrollbar=1&zoom=100`"
                    class="pdf-viewer-tab"
                    @load="onPdfLoadInTab"
                  >
                    <p>
                      Your browser does not support PDF viewing.
                      <a href="/dummy-terms-conditions.pdf" target="_blank"
                        >Click here to download the PDF</a
                      >.
                    </p>
                  </iframe>
                </div>
              </div>

              <!-- PDF Progress Indicator -->
              <div class="scroll-progress-tab mt-3">
                <div class="progress" style="height: 4px">
                  <div
                    class="progress-bar"
                    :class="
                      tabScrollProgress.penalties >= 90
                        ? 'bg-success'
                        : 'bg-primary'
                    "
                    role="progressbar"
                    :style="`width: ${tabScrollProgress.penalties}%`"
                    :aria-valuenow="tabScrollProgress.penalties"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                <small class="text-muted d-block mt-1">
                  {{
                    tabScrollProgress.penalties >= 90
                      ? "Document fully read"
                      : `Reading progress: ${Math.round(
                          tabScrollProgress.penalties
                        )}%`
                  }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Terms and Conditions Checkbox -->
      <div class="terms-agreement mb-4">
        <div
          class="form-check d-flex align-items-start gap-3 p-3 border rounded"
        >
          <input
            id="termsCheckbox"
            v-model="agreedToTerms"
            type="checkbox"
            class="form-check-input mt-1"
            :disabled="!hasReadAllTabs"
            style="transform: scale(1.2)"
          />
          <label
            for="termsCheckbox"
            class="form-check-label flex-grow-1"
            :class="{ 'text-muted': !hasReadAllTabs }"
          >
            <strong>I agree to the Terms and Conditions</strong>
            <p class="mb-0 small mt-1">
              I have read and understood the complete service bond agreement,
              including all terms, conditions, penalties, and obligations. I
              acknowledge that failure to comply with the service bond
              requirements may result in the financial penalties and other
              consequences as outlined in the sections above.
            </p>
          </label>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-end gap-3">
        <b-button variant="outline-secondary" @click="closeModal">
          Cancel
        </b-button>
        <b-button
          variant="warning"
          :disabled="!canProceed"
          @click="proceedWithEnrollment"
        >
          <i class="fe fe-check-circle me-2"></i>
          Accept Terms & Continue Enrollment
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "TermsConditionsTabsModal",

  props: {
    modalId: {
      type: String,
      default: "terms-conditions-tabs-modal",
    },
    trainingData: {
      type: Object,
      default: null,
    },
    enrollmentPayload: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      agreedToTerms: false,
      viewedTabs: [],
      tabScrollProgress: {
        agreement: 0,
        penalties: 0,
      },
      totalTabs: 2,
      pdfLoadedInTab: false,
      autoReadTimeout: null,
    };
  },

  computed: {
    hasReadAllTabs() {
      return (
        this.viewedTabs.length >= this.totalTabs &&
        this.tabScrollProgress.agreement >= 90 &&
        this.tabScrollProgress.penalties >= 90
      );
    },

    tabsReadCount() {
      let count = 0;
      if (
        this.viewedTabs.includes("agreement") &&
        this.tabScrollProgress.agreement >= 90
      )
        count++;
      if (
        this.viewedTabs.includes("penalties") &&
        this.tabScrollProgress.penalties >= 90
      )
        count++;
      return count;
    },

    readingProgress() {
      return (this.tabsReadCount / this.totalTabs) * 100;
    },

    canProceed() {
      return this.hasReadAllTabs && this.agreedToTerms;
    },
  },

  watch: {
    trainingData: {
      immediate: true,
      handler(newData) {
        if (newData) {
          this.resetModalState();
        }
      },
    },
  },

  methods: {
    markTabAsViewed(tabName) {
      if (!this.viewedTabs.includes(tabName)) {
        this.viewedTabs.push(tabName);
      }

      // Start scroll tracking for the current tab
      this.$nextTick(() => {
        this.checkTabScrollPosition(tabName);
      });
    },

    onTabScroll(tabName, event) {
      this.checkTabScrollPosition(tabName, event.target);
    },

    onPdfLoadInTab() {
      this.pdfLoadedInTab = true;
    },

    checkTabScrollPosition(tabName, container = null) {
      let scrollContainer = container;

      if (!scrollContainer) {
        const refName = `${tabName}Content`;
        scrollContainer = this.$refs[refName];
      }

      if (scrollContainer) {
        const scrollTop = scrollContainer.scrollTop;
        const scrollHeight = scrollContainer.scrollHeight;
        const clientHeight = scrollContainer.clientHeight;

        const maxScroll = scrollHeight - clientHeight;

        if (maxScroll > 0) {
          this.tabScrollProgress[tabName] = Math.min(
            (scrollTop / maxScroll) * 100,
            100
          );
        } else {
          // For PDF tab (penalties), give some time to read if no scrolling is needed
          if (tabName === "penalties" && !this.autoReadTimeout) {
            this.autoReadTimeout = setTimeout(() => {
              this.tabScrollProgress[tabName] = 100;
            }, 5000); // 5 seconds delay for PDF content
          } else if (tabName === "agreement") {
            // Regular content tab
            this.tabScrollProgress[tabName] = 100;
          }
        }
      }
    },

    resetModalState() {
      this.agreedToTerms = false;
      this.viewedTabs = [];
      this.tabScrollProgress = {
        agreement: 0,
        penalties: 0,
      };
      this.pdfLoadedInTab = false;

      if (this.autoReadTimeout) {
        clearTimeout(this.autoReadTimeout);
        this.autoReadTimeout = null;
      }

      // Reset scroll position for PDF tab
      if (this.$refs.penaltiesContent) {
        this.$refs.penaltiesContent.scrollTop = 0;
      }
    },

    proceedWithEnrollment() {
      if (!this.canProceed) return;

      // Emit the enrollment payload to parent
      this.$emit("proceed-enrollment", this.enrollmentPayload);
      this.closeModal();
    },

    closeModal() {
      this.$bvModal.hide(this.modalId);
    },

    onHidden() {
      this.resetModalState();
      this.$emit("modal-closed");
    },
  },
};
</script>

<style scoped>
.terms-modal-content {
  padding: 1rem 0;
}

.reading-progress {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  background-color: #f8f9fa;
}

.terms-tabs-container {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  background-color: #fff;
}

.terms-content {
  height: 400px;
  overflow-y: auto;
  padding-right: 1rem;
}

/* Tab Styles */
.nav-tabs {
  border-bottom: 1px solid #dee2e6;
}

.nav-tabs .nav-link {
  color: #718096;
  font-weight: 500;
  border: none !important;
  border-bottom: 3px solid transparent !important;
  padding: 0.75rem 1rem;
  background: transparent !important;
}

.nav-tabs .nav-link:hover {
  border-color: transparent transparent #dee2e6 transparent !important;
  color: #4a5568;
}

.nav-tabs .nav-link.active {
  color: #e63757 !important;
  font-weight: 600;
  border-bottom: 3px solid #e63757 !important;
  background: transparent !important;
}

/* Content Styles */
.section {
  margin-bottom: 2rem;
}

.content-box {
  background-color: #fafafa;
  border: 1px solid #e9ecef !important;
}

.expectations-grid .expectation-item {
  background-color: #f8f9fa;
  transition: all 0.2s;
}

.expectations-grid .expectation-item:hover {
  background-color: #e9ecef;
  transform: translateY(-1px);
}

.exception-item,
.consequence-item {
  transition: all 0.2s;
}

.exception-item:hover,
.consequence-item:hover {
  background-color: #f8f9fa;
  transform: translateX(5px);
}

.cost-breakdown .cost-item {
  transition: all 0.2s;
}

.cost-breakdown .cost-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.payment-term {
  background-color: #f8f9fa;
  transition: all 0.2s;
}

.payment-term:hover {
  background-color: #e9ecef;
  transform: translateY(-1px);
}

.terms-agreement {
  background-color: #f8f9fa;
}

.form-check-input:disabled + .form-check-label {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Alert Styles */
.alert-warning {
  background-color: #fff3cd;
  border-color: #f6c343;
  color: #856404;
}

/* Badge Styles */
.badge.bg-warning {
  background-color: #f6c343 !important;
}

.badge.bg-success {
  background-color: #00d97e !important;
}

.badge.bg-secondary {
  background-color: #6c757d !important;
}

.badge.bg-danger {
  background-color: #e63757 !important;
}

.badge.bg-info {
  background-color: #39afd1 !important;
}

/* PDF Tab Styles */
.pdf-tab-container {
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  background-color: #fff;
}

.pdf-viewer-wrapper-tab {
  position: relative;
  height: 400px;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  overflow: hidden;
}

.pdf-scroll-container-tab {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.pdf-viewer-tab {
  width: 100%;
  height: 120%; /* Make iframe taller to enable scrolling */
  min-height: 480px;
  border: none;
  display: block;
}

.scroll-progress-tab {
  margin-top: 0.5rem;
}

/* Scrollbar Styles */
.terms-content::-webkit-scrollbar,
.pdf-scroll-container-tab::-webkit-scrollbar {
  width: 6px;
}

.terms-content::-webkit-scrollbar-track,
.pdf-scroll-container-tab::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.terms-content::-webkit-scrollbar-thumb,
.pdf-scroll-container-tab::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.terms-content::-webkit-scrollbar-thumb:hover,
.pdf-scroll-container-tab::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
